import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Wifi,
  WifiOff,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  X,
  Clock
} from 'lucide-react';
import { useOfflineStorageV2 } from '@/core/offline/useOfflineStorageV2';

interface OfflineIndicatorProps {
  showWhenOnline?: boolean;
  position?: 'top' | 'bottom';
  className?: string;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  showWhenOnline = false,
  position = 'top',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [lastOnlineState, setLastOnlineState] = useState<boolean | null>(null);

  const {
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncTime,
    syncPendingOperations
  } = useOfflineStorageV2();

  // Gestisci transizioni online/offline con toast
  useEffect(() => {
    if (lastOnlineState !== null && lastOnlineState !== isOnline) {
      if (isOnline) {
        setToastMessage('📡 Connessione ripristinata!');
        if (pendingCount > 0) {
          setToastMessage(`📡 Connessione ripristinata! Sincronizzando ${pendingCount} operazioni...`);
        }
      } else {
        setToastMessage('📵 Modalità offline attiva');
      }
      setShowToast(true);

      // Auto-hide toast dopo 4 secondi
      setTimeout(() => setShowToast(false), 4000);
    }
    setLastOnlineState(isOnline);
  }, [isOnline, pendingCount, lastOnlineState]);

  // Auto-hide quando online se non configurato per mostrarsi sempre
  useEffect(() => {
    if (isOnline && !showWhenOnline && !isSyncing && pendingCount === 0) {
      const timer = setTimeout(() => setIsVisible(false), 2000);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(true);
    }
  }, [isOnline, showWhenOnline, isSyncing, pendingCount]);

  const getStatusIcon = () => {
    if (isSyncing) return <RefreshCw className="h-4 w-4 animate-spin" />;
    if (!isOnline) return <WifiOff className="h-4 w-4" />;
    if (pendingCount > 0) return <Clock className="h-4 w-4" />;
    return <Wifi className="h-4 w-4" />;
  };

  const getStatusColor = () => {
    if (isSyncing) return 'bg-blue-500';
    if (!isOnline) return 'bg-red-500';
    if (pendingCount > 0) return 'bg-orange-500';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    if (isSyncing) return 'Sincronizzazione...';
    if (!isOnline) return 'Offline';
    if (pendingCount > 0) return `${pendingCount} operazioni in coda`;
    return 'Online';
  };

  const getStatusDescription = () => {
    if (isSyncing) return 'Sincronizzando le modifiche con il server';
    if (!isOnline) return 'Le modifiche verranno sincronizzate quando la connessione sarà ripristinata';
    if (pendingCount > 0) return 'Alcune operazioni sono in attesa di sincronizzazione';
    if (lastSyncTime) {
      const lastSync = new Date(lastSyncTime);
      return `Ultimo aggiornamento: ${lastSync.toLocaleTimeString('it-IT')}`;
    }
    return 'Tutte le modifiche sono sincronizzate';
  };

  const handleManualSync = async () => {
    if (isOnline && !isSyncing) {
      await syncPendingOperations();
    }
  };

  if (!isVisible && isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <>
      {/* Banner principale */}
      <div className={`
        fixed ${position === 'top' ? 'top-0' : 'bottom-0'} left-0 right-0 z-50
        transition-transform duration-300 ease-in-out
        ${isVisible ? 'translate-y-0' : position === 'top' ? '-translate-y-full' : 'translate-y-full'}
        ${className}
      `}>
        <Alert className={`
          rounded-none border-x-0 ${position === 'bottom' ? 'border-b-0' : 'border-t-0'}
          ${getStatusColor()} text-white border-transparent
        `}>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {getStatusIcon()}
                <span className="font-medium">{getStatusText()}</span>
              </div>

              {pendingCount > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                >
                  {pendingCount}
                </Badge>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {isOnline && pendingCount > 0 && !isSyncing && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleManualSync}
                  className="text-white hover:bg-white/20 h-7 px-2"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Sincronizza
                </Button>
              )}

              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsVisible(false)}
                className="text-white hover:bg-white/20 h-7 w-7 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>

          <AlertDescription className="text-white/90 text-xs mt-1">
            {getStatusDescription()}
          </AlertDescription>
        </Alert>
      </div>

      {/* Toast per notifiche */}
      {showToast && (
        <div className={`
          fixed ${position === 'top' ? 'top-16' : 'bottom-16'} left-4 right-4 z-50
          transition-all duration-300 ease-in-out
          ${showToast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
        `}>
          <Alert className={`
            ${isOnline ? 'bg-green-500' : 'bg-orange-500'}
            text-white border-transparent shadow-lg
          `}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {isOnline ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                <AlertDescription className="text-white font-medium">
                  {toastMessage}
                </AlertDescription>
              </div>

              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowToast(false)}
                className="text-white hover:bg-white/20 h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </Alert>
        </div>
      )}
    </>
  );
};

export default OfflineIndicator;